/**
 * NOMMER UNE PERSONNE À L'ÉCRAN.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Depuis le 06/09/2026, `rh-personnel.nom` ne porte plus que le PATRONYME
 * (« Sakly »), le prénom vivant dans sa propre colonne. Un écran qui affiche
 * encore `p.nom` écrit donc « Sakly » là où il écrivait « Syrine Sakly » — et
 * il reste parfaitement plausible en le faisant. C'est une régression
 * silencieuse ; ce module est là pour qu'il n'y en ait qu'une seule règle.
 *
 * ⚠️ `nomDe` RETOMBE TOUJOURS SUR SES PIEDS, et c'est délibéré. Toutes les
 * routes ne calculent pas encore `nomAffiche` ; certaines ne renvoient qu'un
 * `{ orgId, nom }` en guise d'étiquette. L'ordre est donc :
 *
 *   1. `nomAffiche` si le serveur l'a composé — la source d'autorité ;
 *   2. sinon `titre + prenom + nom`, recomposés ici ;
 *   3. sinon `nom` tel quel — ce qui, sur une étiquette servie par une route
 *      ancienne, reste le nom complet et s'affiche donc juste.
 *
 * Aucun de ces trois cas ne rend une chaîne vide tant que quelque chose est
 * renseigné : un nom manquant à l'écran se lit « personne », et c'est
 * exactement le contresens à éviter.
 */

export type Nommable = {
  nom?: string | null;
  prenom?: string | null;
  titre?: string | null;
  nomAffiche?: string | null;
};

const net = (s: string | null | undefined) => String(s ?? '').replace(/\s+/g, ' ').trim();

/** Le nom à écrire à l'écran : « Dr Inès Harzallah ». */
export function nomDe(p: Nommable | null | undefined): string {
  if (!p) return '';
  const servi = net(p.nomAffiche);
  if (servi) return servi;
  const compose = [net(p.titre), net(p.prenom), net(p.nom)].filter(Boolean).join(' ');
  return compose || net(p.nom);
}

/** Pour trier et pour chercher : « Harzallah Inès », sans le titre — sinon les
 *  trois « Dr » du laboratoire se rangent ensemble à la lettre D. */
export function nomTriDe(p: Nommable | null | undefined): string {
  if (!p) return '';
  const parts = [net(p.nom), net(p.prenom)].filter(Boolean);
  return parts.length > 1 ? parts.join(' ') : nomDe(p);
}

/** Le prénom seul, pour saluer. Retombe sur le premier mot du nom composé
 *  quand la colonne est vide — mieux vaut « Wafa » que rien. */
export function prenomDe(p: Nommable | null | undefined): string {
  if (!p) return '';
  return net(p.prenom) || nomDe(p).replace(/^(Dr|Pr|Mme|M\.|Mlle)\s+/i, '').split(' ')[0] || '';
}
